import { axiosInstance } from 'src/constants/axiosInstance';
import { ApiResponse } from 'src/types/commonResponseTypes';
import {
  WorkspaceNote,
  WorkspaceNoteFolder,
  WorkspaceNotesPage,
  WorkspaceNoteUpdatePayload,
  WorkspaceNoteWritePayload,
} from 'src/types/workspaceNotes';
import { handleError } from 'src/utils/commonErrorUtils';

const mutationHeaders = (idempotencyKey: string) => ({
  'Idempotency-Key': idempotencyKey,
});

export const listWorkspaceNotes = async (params: {
  page: number;
  limit: number;
  search?: string;
  folderId?: string;
}): Promise<ApiResponse<WorkspaceNotesPage>> => {
  try {
    const { data } = await axiosInstance.get('/v1/workspace-notes', {
      params,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getWorkspaceNote = async (
  noteId: string,
): Promise<ApiResponse<WorkspaceNote>> => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/workspace-notes/${encodeURIComponent(noteId)}`,
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const listWorkspaceNoteFolders = async (
  search?: string,
): Promise<ApiResponse<WorkspaceNoteFolder[]>> => {
  try {
    const { data } = await axiosInstance.get('/v1/workspace-notes/folders', {
      params: { search: search || undefined },
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createWorkspaceNoteFolder = async (
  name: string,
  idempotencyKey: string,
): Promise<ApiResponse<WorkspaceNoteFolder>> => {
  try {
    const { data } = await axiosInstance.post(
      '/v1/workspace-notes/folders',
      { name },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const renameWorkspaceNoteFolder = async (
  folderId: string,
  name: string,
): Promise<ApiResponse<WorkspaceNoteFolder>> => {
  try {
    const { data } = await axiosInstance.patch(
      `/v1/workspace-notes/folders/${encodeURIComponent(folderId)}`,
      { name },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteWorkspaceNoteFolder = async (
  folderId: string,
): Promise<ApiResponse<{ movedToWorkspace: boolean }>> => {
  try {
    const { data } = await axiosInstance.delete(
      `/v1/workspace-notes/folders/${encodeURIComponent(folderId)}`,
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createWorkspaceNote = async (
  payload: WorkspaceNoteWritePayload,
  idempotencyKey: string,
): Promise<ApiResponse<WorkspaceNote>> => {
  try {
    const { data } = await axiosInstance.post('/v1/workspace-notes', payload, {
      headers: mutationHeaders(idempotencyKey),
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateWorkspaceNote = async (
  noteId: string,
  payload: WorkspaceNoteUpdatePayload,
): Promise<ApiResponse<WorkspaceNote>> => {
  try {
    const { data } = await axiosInstance.patch(
      `/v1/workspace-notes/${encodeURIComponent(noteId)}`,
      payload,
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteWorkspaceNote = async (
  noteId: string,
  revision: number,
): Promise<ApiResponse<WorkspaceNote>> => {
  try {
    const { data } = await axiosInstance.delete(
      `/v1/workspace-notes/${encodeURIComponent(noteId)}`,
      { data: { revision } },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const retryWorkspaceNoteIndex = async (
  noteId: string,
): Promise<ApiResponse<WorkspaceNote>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/workspace-notes/${encodeURIComponent(noteId)}/retry-index`,
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};
